import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useRef, useState } from 'react';
import * as EmployeeService from 'services/EmployeeService';
import ESignaturePad, { ESignaturePadHandle } from './ESignaturePad';

interface SignatureGateProps {
    employeeId: number;
    employeeName: string;
    onSignatureComplete: () => void;
}

export default function SignatureGate({ employeeId, employeeName, onSignatureComplete }: SignatureGateProps) {
    const { keycloak } = useKeycloak();
    const signaturePadRef = useRef<ESignaturePadHandle>(null);
    const [isValid, setIsValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        const dataUrl = signaturePadRef.current?.getSignatureDataUrl();
        if (!dataUrl || !keycloak?.token) return;

        setIsSubmitting(true);
        setError(null);

        const result = await EmployeeService.saveSignature(
            keycloak.token,
            () => keycloak.logout(),
            employeeId,
            dataUrl
        );

        setIsSubmitting(false);

        if (result) {
            onSignatureComplete();
        } else {
            setError('Impossibile salvare la firma. Riprova.');
        }
    };

    return (
        <Flex minH="100vh" align="center" justify="center" bg="gray.50" px={{ base: '16px', md: '0px' }}>
            <Box bg="white" borderRadius="20px" boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.18)" p={8} w="full" maxW="lg">
                <Text fontSize="2xl" fontWeight="bold" color="#9FB50C">
                    Crea la tua firma digitale
                </Text>
                <Text color="gray.500" mt={2} mb={6} fontSize="sm">
                    Prima di accedere devi creare la tua firma. Verrà usata per i documenti aziendali.
                </Text>

                <ESignaturePad
                    ref={signaturePadRef}
                    signerName={employeeName}
                    disabled={isSubmitting}
                    onChange={({ isValid }) => setIsValid(isValid)}
                />

                {error && (
                    <Text fontSize="sm" color="red.500" mt={3}>
                        {error}
                    </Text>
                )}

                <Button
                    onClick={handleSubmit}
                    isDisabled={!isValid || isSubmitting}
                    isLoading={isSubmitting}
                    _hover={{ bg: 'gray.700' }}
                    _active={{ bg: 'gray.600' }}
                    _focus={{ boxShadow: 'none' }}
                    backgroundColor="#688CC8"
                    color="white"
                    width="100%"
                    height="40px"
                    borderRadius="6px"
                    mt={6}
                >
                    Salva firma e accedi
                </Button>

                <Flex justify="center" mt={4}>
                    <Text
                        as="button"
                        onClick={() => keycloak?.logout()}
                        fontSize="xs"
                        color="gray.400"
                        textDecoration="underline"
                        _hover={{ color: 'gray.600' }}
                    >
                        Non sei tu? Esci
                    </Text>
                </Flex>
            </Box>
        </Flex>
    );
}
